import { Injectable } from "@angular/core";
import { DangerError } from "@shared/errors/custom.error";
import { TipoDocumento } from "@models/tipo-documento.model";
import IRegion from "@models/region.interface";
import { ITerminologia } from "@models/usuario.interface";
import IEstado, { IEstadoApelacionParcial, IEstadoExpediente } from "@models/estado.interface";
import IMotivo from "@models/motivo.interface";
import { ITipoNotificacion } from "@models/tipo.interface";
import { assignToModel } from "@shared/utils/fieldsMap.utils";
import TiposDocumento from "@shared/enums/tipos-documento.enum";
import IRoles from "@models/roles.interface";
import Roles from "@shared/enums/roles.enum";
import IMensaje from "@models/mensaje.interface";
import { Mensajes } from "@shared/enums/mensajes.enum";
import ICompin from "@models/compin.interface";
import { ApelacionEstados } from "@shared/enums/apelacion-estados.enum";
import { ApelacionParcialEstados } from "@shared/enums/apelacion-parcial-estados.enum";

@Injectable({
    providedIn: "root",
})
export class TerminologiaService {
    private terminologia: ITerminologia;
    private regiones: IRegion[] = [];
    private estados_documento: IEstado[] = [];
    private estados_expediente: IEstadoExpediente[] = [];
    private estados_licencia: IEstado[] = [];
    private estados_resolucion: IEstado[] = [];
    private estados_apelacion: IEstado[] = [];
    private estados_apelacion_parcial: IEstadoApelacionParcial[] = [];
    private motivos_rechazo_documento: IMotivo[] = [];
    private motivos_solicitud_prorroga: IMotivo[] = [];
    private motivos_rechazo_expediente: IMotivo[] = [];
    private tipos_documento: TipoDocumento[] = [];
    private tipo_documento_prorroga: TipoDocumento;
    private tipo_documento_apelacion: TipoDocumento;
    private tipos_notificacion: ITipoNotificacion[] = [];
    private roles: IRoles[] = [];
    private mensajes: IMensaje[] = [];
    private compines: ICompin[] = [];

    private obtenerTerminologia(msg: string = "Terminologia no cargada en el sistema") {
        if (this.terminologia) return this.terminologia;

        const cache = sessionStorage.getItem("terminologia");
        if (!cache) throw new DangerError(msg);
        const cacheObject = JSON.parse(cache);
        if (Object.keys(cacheObject).length === 0) throw new DangerError(msg);
        this.terminologia = cacheObject;
    }

    obtenerRegiones(): IRegion[] {
        if (this.regiones.length > 0) return this.regiones;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.regiones;
        if (!cache || cache.length === 0) throw new DangerError("Regiones no encontradas");
        this.regiones = cache;
        return this.regiones;
    }

    obtenerEstadosDocumento(): IEstado[] {
        if (this.estados_documento.length > 0) return this.estados_documento;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.estados_documento;
        if (!cache || cache.length === 0) throw new DangerError("Estados documento no encontrados");
        this.estados_documento = cache;
        return this.estados_documento;
    }

    obtenerEstadosExpediente(): IEstadoExpediente[] {
        if (this.estados_expediente.length > 0) return this.estados_expediente;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.estados_expediente;
        if (!cache || cache.length === 0) throw new DangerError("Estados expediente no encontrados");
        this.estados_expediente = cache;
        return this.estados_expediente;
    }

    obtenerEstadosLicencia(): IEstado[] {
        if (this.estados_licencia.length > 0) return this.estados_licencia;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.estados_licencia;
        if (!cache || cache.length === 0) throw new DangerError("Estados licencia no encontrados");
        this.estados_licencia = cache;
        return this.estados_licencia;
    }

    obtenerEstadosLicenciaPorID(id: number): IEstado | undefined {
        if (this.estados_licencia.length === 0) this.obtenerEstadosLicencia();
        return this.estados_licencia.find(el => el.codigo === id);
    }

    obtenerEstadosResolucion(): IEstado[] {
        if (this.estados_resolucion.length > 0) return this.estados_resolucion;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.estados_resolucion;
        if (!cache || cache.length === 0) throw new DangerError("Estados resolución no encontrados");
        this.estados_resolucion = cache;
        return this.estados_resolucion;
    }

    obtenerEstadosApelacion(): IEstado[] {
        if (this.estados_apelacion.length > 0) return this.estados_apelacion;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.estados_apelacion;
        if (!cache || cache.length === 0) throw new DangerError("Estados apelación no encontrados");
        this.estados_apelacion = cache;
        return this.estados_apelacion;
    }

    obtenerEstadoApelacionPorID(ids: ApelacionEstados[]): IEstado[] {
        if (this.estados_apelacion.length === 0) this.obtenerEstadosApelacion();
        return this.estados_apelacion.filter(c => ids.includes(c.codigo));
    }

    obtenerEstadosApelacionParcial(): IEstadoApelacionParcial[] {
        if (this.estados_apelacion_parcial.length > 0) return this.estados_apelacion_parcial;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.estados_apelacion_parcial;
        if (!cache || cache.length === 0) throw new DangerError("Estados apelación parcial no encontrados");
        this.estados_apelacion_parcial = cache;
        return this.estados_apelacion_parcial;
    }

    obtenerEstadoApelacionParcialPorID(id: ApelacionParcialEstados): IEstadoApelacionParcial[] {
        if (this.estados_apelacion_parcial.length === 0) this.obtenerEstadosApelacion();
        return this.estados_apelacion_parcial.filter(c => id === c.codigo);
    }

    obtenerMotivosRechazoDocumento(): IMotivo[] {
        if (this.motivos_rechazo_documento.length > 0) return this.motivos_rechazo_documento;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.motivos_rechazo_documento;
        if (!cache || cache.length === 0) throw new DangerError("Motivos rechazo documento no encontrados");
        this.motivos_rechazo_documento = cache;
        return this.motivos_rechazo_documento.sort((a, b) => a.codigo - b.codigo);
    }

    obtenerMotivosRechazoExpediente(): IMotivo[] {
        if (this.motivos_rechazo_expediente.length > 0) return this.motivos_rechazo_expediente;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.motivos_rechazo_expediente;
        if (!cache || cache.length === 0) throw new DangerError("Motivos rechazo expediente no encontrados");
        this.motivos_rechazo_expediente = cache;
        return this.motivos_rechazo_expediente.sort((a, b) => a.codigo - b.codigo);
    }

    obtenerMotivosSolicitudProrroga(): IMotivo[] {
        if (this.motivos_solicitud_prorroga.length > 0) return this.motivos_solicitud_prorroga;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.motivos_solicitud_prorroga;
        if (!cache || cache.length === 0) throw new DangerError("Motivos solicitud prórroga no encontrados");
        this.motivos_solicitud_prorroga = cache;
        return this.motivos_solicitud_prorroga;
    }

    obtenerTiposDocumentoLicencia(): TipoDocumento[] {
        if (this.tipos_documento.length > 0) return this.tipos_documento;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.tipos_documento;
        if (!cache || cache.length === 0) throw new DangerError("Tipos de documentos no encontrados");
        this.tipos_documento = cache.filter((c) => c.codigo !== TiposDocumento.solicitud_prorroga && c.codigo !== TiposDocumento.solicitud_apelacion)
        .map(c => assignToModel(TipoDocumento, c));
        return this.tipos_documento;
    }

    obtenerTipoDocumentoProrroga(): TipoDocumento {
        if (this.tipo_documento_prorroga) return this.tipo_documento_prorroga;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.tipos_documento;
        if (!cache || cache.length === 0) throw new DangerError("Tipos de documentos no encontrados");
        this.tipo_documento_prorroga = assignToModel(TipoDocumento, cache.find((c) => c.codigo === TiposDocumento.solicitud_prorroga));
        return this.tipo_documento_prorroga;
    }

    obtenerTiposNotificacion(): ITipoNotificacion[] {
        if (this.tipos_notificacion.length > 0) return this.tipos_notificacion;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.tipos_notificacion;
        if (!cache || cache.length === 0) throw new DangerError("Tipos de documentos no encontrados");
        this.tipos_notificacion = cache;
        return this.tipos_notificacion;
    }

    obtenerRoles(): IRoles[] {
        if (this.roles.length > 0) return this.roles;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.roles;
        if (!cache || cache.length === 0) throw new DangerError("Roles no encontrados");
        this.roles = cache;
        return this.roles;
    }

    obtenerRolPorID(id: Roles): IRoles {
        if (this.roles.length > 0) return this.roles.find(r => r.codigo === id);
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.roles;
        if (!cache || cache.length === 0) throw new DangerError("Roles no encontrados");
        this.roles = cache;
        return this.roles.find(r => r.codigo === id);
    }

    obtenerMensajes(): IMensaje[] {
        if (this.mensajes.length > 0) return this.mensajes;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.mensajes;
        if (!cache || cache.length === 0) throw new DangerError("Roles no encontrados");
        this.mensajes = cache;
        return this.mensajes;
    }

    obtenerTipoDocumentoResultadoApelacion(): TipoDocumento {
        if (this.tipo_documento_apelacion) return this.tipo_documento_apelacion;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.tipos_documento;
        if (!cache || cache.length === 0) throw new DangerError("Tipos de documentos no encontrados");
        this.tipo_documento_apelacion = assignToModel(TipoDocumento, cache.find((c) => c.codigo === TiposDocumento.resultado_apelacion));
        return this.tipo_documento_apelacion;
    }

    obtenerMensajePorID(id: Mensajes): string {
        if (this.mensajes.length === 0) this.obtenerMensajes();
        return this.mensajes.find(r => r.codigo === id)?.nombre;
    }

    obtenerCompines(): ICompin[] {
        if (this.compines.length > 0) return this.compines;
        if (!this.terminologia) this.obtenerTerminologia();

        const cache = this.terminologia.compines;
        if (!cache || cache.length === 0) throw new DangerError("Compines no encontrados");
        this.compines = cache;
        return this.compines;
    }

    obtenerCompinesPorID(ids: number[]): ICompin[] {
        if (this.compines.length === 0) this.obtenerCompines();
        return this.compines.filter(c => ids.includes(c.codigo));
    }
}
